import { formatMontant } from '@mairistem/finances-utils';
import { groupBy } from 'lodash';
import MotifNonImport from '../../constants/simulation/motifNonImport';
import type ImputationHorsEspace from '../../interfaces/simulation/imputationHorsEspace';
import type ImputationNonTrouvee from '../../interfaces/simulation/imputationNonTrouvee';
import type ServiceNonHabilite from '../../interfaces/simulation/serviceNonHabilite';
import { getLibelleCodesImputation } from '../imputation';

// Contenu du "resultat" (JSON) d'un job d'import APCP ou de reprise des RAR
export interface ResultatRapport {
	imputationsNonTrouvees: ImputationNonTrouvee[];
	// Lignes dont le service n'a aucun espace contenant l'imputation (import APCP seulement)
	imputationsHorsEspace: ImputationHorsEspace[];
	// Restes a realiser repris sur "Aucun service" (reprise des RAR seulement)
	servicesNonHabilites: ServiceNonHabilite[];
}

/**
 * Lit le "resultat" (JSON) d'un job d'import APCP ou de reprise des RAR termine
 * avec succes. Une liste est vide quand il n'y a pas de resultat a lire :
 * - null : back pas encore a jour, qui ne renseigne pas le resultat
 * - undefined : recopie des reports, qui utilise la meme modal sans transmettre de resultat
 * - cle absente : back pas encore a jour, import APCP sans servicesNonHabilites
 *   ou reprise des RAR sans imputationsHorsEspace
 */
export const parseResultatRapport = (resultat?: unknown): ResultatRapport => {
	const parsed = typeof resultat === 'string' ? JSON.parse(resultat) : {};

	return {
		imputationsNonTrouvees: parsed.imputationsNonTrouvees ?? [],
		imputationsHorsEspace: parsed.imputationsHorsEspace ?? [],
		servicesNonHabilites: parsed.servicesNonHabilites ?? [],
	};
};

// Le rapport s'affiche des qu'une liste est renseignee : chaque job ne
// renseigne que les siennes
export const hasLignesRapport = (rapport: ResultatRapport): boolean =>
	rapport.imputationsNonTrouvees.length > 0 ||
	rapport.imputationsHorsEspace.length > 0 ||
	rapport.servicesNonHabilites.length > 0;

// Par exemple : "2 lignes APCP n'ont pas été importées"
const getLignesNonImportees = (count: number): string =>
	count > 1
		? `${count} lignes APCP n'ont pas été importées`
		: "1 ligne APCP n'a pas été importée";

const getLibelleExercice = (anneeExercice?: number): string =>
	anneeExercice ? `l'exercice ${anneeExercice}` : "l'exercice";

// Ligne hors espace sans motif (job anterieur a son ajout)
const HORS_ESPACE_SANS_MOTIF = 'hors_espace_sans_motif';

type CleMotif = MotifNonImport | typeof HORS_ESPACE_SANS_MOTIF;

// Cause affichee dans le resume, dans l'ordre des controles du back. Une ligne
// hors espace sans motif garde le libelle d'origine de sa liste
const CAUSES_MOTIFS: Record<CleMotif, string> = {
	[MotifNonImport.IMPUTATION_NON_TROUVEE]: 'aucune imputation équivalente',
	[MotifNonImport.IMPUTATION_CADUQUE]: "l'imputation équivalente est caduque",
	[MotifNonImport.SERVICE_SANS_ESPACE]: "le service n'a aucun espace",
	[MotifNonImport.IMPUTATION_HORS_ESPACE]:
		"l'imputation n'est rattachée à aucun espace du service",
	[HORS_ESPACE_SANS_MOTIF]:
		"le service n'est habilité sur aucun espace contenant l'imputation",
};

// Un motif absent ou inconnu prend celui par defaut de la liste de la ligne
const getCleMotif = (
	ligne: ImputationNonTrouvee,
	defaut: CleMotif,
): CleMotif =>
	ligne.motif && ligne.motif in CAUSES_MOTIFS ? ligne.motif : defaut;

export interface GroupeLignesNonImportees {
	motif: CleMotif;
	lignes: ImputationNonTrouvee[];
	// Par exemple : "2 lignes APCP n'ont pas été importées : l'imputation
	// équivalente est caduque dans l'exercice 2027."
	resume: string;
}

/**
 * Regroupe par motif les lignes APCP non importees ou non reprises, dans
 * l'ordre des controles du back. Seuls les motifs presents sont renvoyes
 * @param anneeExercice Annee de l'exercice de la simulation
 */
export const getGroupesLignesNonImportees = (
	imputationsNonTrouvees: ImputationNonTrouvee[],
	imputationsHorsEspace: ImputationHorsEspace[],
	anneeExercice?: number,
): GroupeLignesNonImportees[] => {
	const nonTrouveesParMotif = groupBy(imputationsNonTrouvees, (ligne) =>
		getCleMotif(ligne, MotifNonImport.IMPUTATION_NON_TROUVEE),
	);
	const horsEspaceParMotif = groupBy(imputationsHorsEspace, (ligne) =>
		getCleMotif(ligne, HORS_ESPACE_SANS_MOTIF),
	);

	return (Object.keys(CAUSES_MOTIFS) as CleMotif[]).flatMap((motif) => {
		const lignesMotif = [
			...(nonTrouveesParMotif[motif] ?? []),
			...(horsEspaceParMotif[motif] ?? []),
		];
		if (lignesMotif.length === 0) return [];

		return {
			motif,
			lignes: lignesMotif,
			resume: `${getLignesNonImportees(lignesMotif.length)} : ${CAUSES_MOTIFS[motif]} dans ${getLibelleExercice(anneeExercice)}.`,
		};
	});
};

// Les listes du resultat portent les memes codes d'imputation
const getCodesImputation = (ligne: ImputationNonTrouvee | ServiceNonHabilite) =>
	getLibelleCodesImputation(
		ligne.sens,
		ligne.section,
		ligne.chapitre,
		ligne.article,
		ligne.operation,
		ligne.fonction,
		ligne.ventilation,
		ligne.analytique,
	);

// Par exemple : "D F 011 6999 01 / Service voirie - AP 2 - Rénovation école" (HCV/HCA sont masques)
export const getLibelleImputationNonTrouvee = (
	imputation: ImputationNonTrouvee,
): string => {
	// Le service suit l'imputation, comme dans le rapport du transfert en comptabilite
	const imputationService = [
		getCodesImputation(imputation),
		imputation.libelleService,
	]
		.filter(Boolean)
		.join(' / ');

	return `${imputationService} - ${imputation.libelleProjet}`;
};

// La reprise se fait sur un BP, un BS ou une DM : le budget n'est pas nomme
export const getResumeServicesNonHabilites = (
	services: ServiceNonHabilite[],
): string =>
	services.length > 1
		? `${services.length} restes à réaliser ont été repris sur « Aucun service » : leur service n'est plus habilité sur la ligne.`
		: "1 reste à réaliser a été repris sur « Aucun service » : son service n'est plus habilité sur la ligne.";

// Par exemple : "D I 20 2031 OPNI 313 / SPORT : 200,00", le service suit
// l'imputation comme dans les autres rapports
export const getLibelleServiceNonHabilite = (
	service: ServiceNonHabilite,
): string =>
	`${getCodesImputation(service)} / ${service.libelleService ?? 'Service supprimé'} : ${formatMontant(service.rep)}`;
